// src/pages/ResetPassword.jsx - Reset Password Page
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setError('No reset token provided');
      setValidating(false);
      return;
    }

    try {
      const response = await api.post('/auth/validate-reset-token', { token });
      setTokenValid(true);
      setUserEmail(response.data.email);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired reset token');
      setTokenValid(false);
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: password
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #6B3FA0 0%, #4A2B70 100%)'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '40px',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(74, 43, 112, 0.3)',
          maxWidth: '400px'
        }}>
          <div style={{
            display: 'inline-block',
            width: '40px',
            height: '40px',
            border: '4px solid #E5D5F5',
            borderTopColor: '#6B3FA0',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }}></div>
          <p style={{ marginTop: '20px', color: '#666' }}>Validating reset token...</p>
          <style>
            {`@keyframes spin { to { transform: rotate(360deg); } }`}
          </style>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #6B3FA0 0%, #4A2B70 100%)',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(74, 43, 112, 0.3)',
          width: '100%',
          maxWidth: '450px',
          overflow: 'hidden'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #F3EEFA 0%, #ffffff 100%)',
            padding: '40px',
            textAlign: 'center',
            borderBottom: '3px solid #E74C3C'
          }}>
            <h1 style={{ 
              margin: 0,
              fontSize: '28px',
              color: '#E74C3C',
              fontWeight: '700'
            }}>
              Invalid Token
            </h1>
          </div>

          <div style={{ padding: '40px', textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 20px',
              background: 'linear-gradient(135deg, #E74C3C 0%, #C0392B 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </div>

            <p style={{ color: '#666', marginBottom: '25px', lineHeight: '1.6' }}>
              {error}
            </p>

            <p style={{ color: '#999', fontSize: '14px', marginBottom: '25px' }}>
              The password reset link may have expired or already been used. Please request a new one.
            </p>

            <Link to="/forgot-password">
              <button
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: '#6B3FA0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginBottom: '15px',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#5A2F8A'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#6B3FA0'}
              >
                Request New Reset Link
              </button>
            </Link>

            <Link to="/login" style={{ color: '#6B3FA0', textDecoration: 'none', fontSize: '14px', fontWeight: '600' }}>
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #6B3FA0 0%, #4A2B70 100%)',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(74, 43, 112, 0.3)',
          width: '100%',
          maxWidth: '450px',
          overflow: 'hidden'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #F3EEFA 0%, #ffffff 100%)',
            padding: '40px',
            textAlign: 'center',
            borderBottom: '3px solid #27AE60'
          }}>
            <h1 style={{ 
              margin: 0,
              fontSize: '28px',
              color: '#27AE60',
              fontWeight: '700'
            }}>
              Success!
            </h1>
          </div>

          <div style={{ padding: '40px', textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 20px',
              background: 'linear-gradient(135deg, #27AE60 0%, #1E8449 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>

            <h2 style={{ color: '#4A2B70', marginBottom: '15px' }}>
              Password Reset Complete
            </h2>

            <p style={{ color: '#666', marginBottom: '25px', lineHeight: '1.6' }}>
              Your password has been successfully reset. You can now log in with your new password.
            </p>

            <p style={{ color: '#999', fontSize: '14px' }}>
              Redirecting to login page...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #6B3FA0 0%, #4A2B70 100%)',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 20px 60px rgba(74, 43, 112, 0.3)',
        width: '100%',
        maxWidth: '450px',
        overflow: 'hidden'
      }}>
        {/* Header Section */}
        <div style={{
          background: 'linear-gradient(135deg, #F3EEFA 0%, #ffffff 100%)',
          padding: '40px',
          textAlign: 'center',
          borderBottom: '3px solid #6B3FA0'
        }}>
          <img 
            src="/images/342px_x_216px-01.png" 
            alt="Kyber Security" 
            style={{ 
              height: '60px', 
              width: 'auto',
              marginBottom: '20px'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <h1 style={{ 
            margin: 0, 
            marginBottom: '8px',
            fontSize: '28px',
            color: '#4A2B70',
            fontWeight: '700'
          }}>
            Reset Password
          </h1>
          <p style={{ 
            margin: 0, 
            fontSize: '14px', 
            color: '#7E4DB3'
          }}>
            for {userEmail}
          </p>
        </div>

        {/* Form Section */}
        <div style={{ padding: '40px' }}>
          {error && (
            <div style={{
              padding: '15px',
              marginBottom: '25px',
              backgroundColor: '#FEE',
              border: '1px solid #FCC',
              borderLeft: '4px solid #E74C3C',
              borderRadius: '6px',
              color: '#C00',
              fontSize: '14px'
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600',
                color: '#4A2B70',
                fontSize: '14px'
              }}>
                New Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter new password"
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  border: '2px solid #E5D5F5',
                  borderRadius: '8px',
                  fontSize: '15px',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#6B3FA0';
                  e.target.style.boxShadow = '0 0 0 3px rgba(107, 63, 160, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#E5D5F5';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <small style={{ color: '#999', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                Minimum 8 characters
              </small>
            </div>

            <div style={{ marginBottom: '25px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600',
                color: '#4A2B70',
                fontSize: '14px'
              }}>
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm new password"
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  border: '2px solid #E5D5F5',
                  borderRadius: '8px',
                  fontSize: '15px',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#6B3FA0';
                  e.target.style.boxShadow = '0 0 0 3px rgba(107, 63, 160, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#E5D5F5';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: loading ? '#9B7BB8' : '#6B3FA0',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(107, 63, 160, 0.3)',
                marginBottom: '15px'
              }}
              onMouseOver={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = '#5A2F8A';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(107, 63, 160, 0.4)';
                }
              }}
              onMouseOut={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = '#6B3FA0';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(107, 63, 160, 0.3)';
                }
              }}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  <span style={{
                    display: 'inline-block',
                    width: '16px',
                    height: '16px',
                    border: '2px solid white',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 0.6s linear infinite'
                  }}></span>
                  Resetting...
                </span>
              ) : (
                'Reset Password'
              )}
            </button>

            <div style={{ textAlign: 'center' }}>
              <Link 
                to="/login" 
                style={{
                  color: '#6B3FA0',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Back to Sign In
              </Link>
            </div>
          </form>
        </div>

        {/* Footer Section */}
        <div style={{
          padding: '20px 40px',
          backgroundColor: '#F3EEFA',
          borderTop: '1px solid #E5D5F5',
          textAlign: 'center'
        }}>
          <p style={{ 
            margin: 0, 
            fontSize: '13px', 
            color: '#7E4DB3',
            lineHeight: '1.5'
          }}>
            Need help? Contact <a href="mailto:support@kybersecure.com" style={{ color: '#6B3FA0', fontWeight: '600' }}>support@kybersecure.com</a>
          </p>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default ResetPassword;
